// AudioRecorder.jsx
import React, { useState, useRef, useEffect } from 'react';
import reviewService from '../../services/reviewService';

const AudioRecorder = ({ onRecorded, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [permissionState, setPermissionState] = useState('idle'); // idle | requesting | granted | denied

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  const MAX_SECONDS = 120; // 2 minutes max

  /* ── Cleanup on unmount ── */
  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Timer while recording ── */
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, isPaused]);

  /* ── Waveform visualiser ── */
  const drawWaveform = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const W = canvas.width;
    const H = canvas.height;

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.fillStyle = '#F5F5F5';
      ctx.fillRect(0, 0, W, H);

      ctx.lineWidth = 2;
      ctx.strokeStyle = '#55000C';
      ctx.beginPath();

      const sliceWidth = W / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * H) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.lineTo(W, H / 2);
      ctx.stroke();
    };
    draw();
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  /* ── Start recording ── */
  const startRecording = async () => {
    setError('');
    setPermissionState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermissionState('granted');

      // Set up analyser for waveform
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';

      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mimeType || 'audio/webm'
        });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        stopStream();
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
          audioCtxRef.current.close().catch(() => {});
        }
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setElapsed(0);
      setIsRecording(true);
      setIsPaused(false);

      // Begin waveform
      drawWaveform();
    } catch (err) {
      console.error('Mic access failed:', err);
      setPermissionState('denied');
      setError(
        'Microphone access denied. Please allow mic permissions in your browser.'
      );
    }
  };

  /* ── Pause / Resume ── */
  const togglePause = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    if (isPaused) {
      rec.resume();
      setIsPaused(false);
    } else {
      rec.pause();
      setIsPaused(true);
    }
  };

  /* ── Stop recording ── */
  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    setIsRecording(false);
    setIsPaused(false);
  };

  /* ── Discard & re-record ── */
  const discardRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl('');
    setElapsed(0);
    setError('');
  };

  /* ── Save to Cloudinary ── */
  const handleSave = async () => {
    if (!recordedBlob) return;
    setIsSaving(true);
    setError('');
    try {
      const ext = (recordedBlob.type.includes('webm') && 'webm') || 'wav';
      const file = new File([recordedBlob], `recording-${Date.now()}.${ext}`, {
        type: recordedBlob.type || 'audio/webm'
      });

      const result = await reviewService.uploadAudioToCloudinary(file);

      onRecorded({
        url: result.url,
        publicId: result.publicId,
        name: file.name,
        duration: result.duration || elapsed
      });
    } catch (err) {
      console.error('Save recording failed:', err);
      setError('Failed to save recording. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="border border-[#E4E4E4] bg-[#F9F6F2] p-4 rounded">
      {/* Title row */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-[#1C2A46]">
          Record Audio Message
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-[#6B635D] hover:underline"
          disabled={isRecording || isSaving}
        >
          Cancel
        </button>
      </div>

      {/* Recorder UI */}
      {!recordedBlob ? (
        <>
          <canvas
            ref={canvasRef}
            width={600}
            height={80}
            className="w-full h-20 border border-[#E4E4E4] bg-[#F5F5F5] mb-3"
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${
                  isRecording && !isPaused
                    ? 'bg-red-600 animate-pulse'
                    : 'bg-gray-400'
                }`}
              />
              <span className="text-sm text-[#1C2A46] font-medium tabular-nums">
                {formatTime(elapsed)} / {formatTime(MAX_SECONDS)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {!isRecording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={permissionState === 'requesting'}
                  className="px-4 py-2 text-sm font-medium bg-[#55000C] text-white hover:bg-[#6a0a17] transition-colors disabled:opacity-60"
                >
                  {permissionState === 'requesting'
                    ? 'Requesting mic…'
                    : 'Start Recording'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={togglePause}
                    className="px-3 py-2 text-sm font-medium border border-[#55000C] text-[#55000C] hover:bg-[#F5F0F0] transition-colors"
                  >
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="px-4 py-2 text-sm font-medium bg-[#55000C] text-white hover:bg-[#6a0a17] transition-colors"
                  >
                    Stop
                  </button>
                </>
              )}
            </div>
          </div>

          {error && (
            <p className="mt-2 text-xs text-red-600 text-left">{error}</p>
          )}
        </>
      ) : (
        <>
          <audio
            src={recordedUrl}
            controls
            className="w-full mb-3"
          />
          <div className="flex items-center justify-between text-xs text-[#6B635D] mb-3">
            <span>Recorded: {formatTime(elapsed)}</span>
            <button
              type="button"
              onClick={discardRecording}
              className="text-red-600 hover:underline"
              disabled={isSaving}
            >
              Re-record
            </button>
          </div>

          {error && (
            <p className="mb-2 text-xs text-red-600 text-left">{error}</p>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full py-2 text-sm font-medium transition-colors ${
              isSaving
                ? 'bg-[#C4956A] text-white cursor-wait'
                : 'bg-[#D2A146] text-white hover:bg-[#C49660]'
            }`}
          >
            {isSaving ? 'Saving…' : 'Save Recording'}
          </button>
        </>
      )}
    </div>
  );
};

export default AudioRecorder;